import React from 'react';
import { useCertificateSummary } from '../features/dashboard/hooks/useCertificateSummary';
import SkeletonBlock from '../components/SkeletonBlock';
import { useNavigate } from 'react-router-dom';
import { Tag } from 'primereact/tag';

import type { Client, Agency, Agent } from '../features/dashboard/types/dashboard';
import { useViewMode, type ViewMode } from '../hooks/useViewMode';
import { useSelectedClient } from '../hooks/useSelectedClient';
import '../styles/layout.css';
import ErrorState from 'components/ErrorState';
import { formatUsPhone } from '../utilities/formatPhone';
import '../styles/utilities.css';

export type DetailsSidebarProps = {
  client: Client;
  agency: Agency;
  agent: Agent;
  certificateSummary?: {
    activeCount: number;
    pendingCount: number;
    totalIssuedLabel: string;
    expiringSoonCount: number;
  };
};

function ViewToggle() {
  const { viewMode, setViewMode } = useViewMode();
  const navigate = useNavigate();

  const handleChange = (mode: ViewMode) => {
    if (mode === viewMode) return;

    setViewMode(mode);
    navigate(mode === 'agent' ? '/agentdashboard' : '/insureddashboard');
  };

  return (
    <div className="view-toggle">
      <button
        type="button"
        className={viewMode === 'agent' ? 'view-toggle__btn view-toggle__btn--active' : 'view-toggle__btn'}
        onClick={() => handleChange('agent')}
      >
        <i className="pi pi-eye" aria-hidden="true" />
        Agent View
      </button>
      {/* <button
        type="button"
        className={viewMode === 'insured' ? 'view-toggle__btn view-toggle__btn--active' : 'view-toggle__btn'}
        onClick={() => handleChange('insured')}
      >
        <i className="pi pi-user" aria-hidden="true" />
        Insured View
      </button> */}
    </div>
  );
}

function ClientCard({ client }: { client: Client }) {
  return (
    <div className="card card--md">
      <div className="entity-header">
        <div className="entity-header__icon entity-header__icon--primary">
          <i className="pi pi-building" aria-hidden="true" />
        </div>
        <div className="entity-header__main">
          <div className="entity-header__name truncate">{client.name}</div>
          <div className="entity-header__sub">{client.typeLabel}</div>
        </div>
        <Tag value="Active" severity="success" />
      </div>

      <div className="mt-lg">
        <div className="info-row">
          <i className="info-row__icon pi pi-user" aria-hidden="true" />
          <div className="info-row__content">
            <div className="info-row__label">Primary Contact</div>
            <div className="info-row__value">{client.primaryContactName}</div>
          </div>
        </div>
      </div>

      <div className="mt-md">
        <div className="info-row">
          <i className="info-row__icon pi pi-map-marker" aria-hidden="true" />
          <div className="info-row__content">
            <div className="info-row__value">{client.addressLine1}</div>
            {client.addressLine2 && (
              <div className="info-row__value info-row__value--muted">{client.addressLine2}</div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-md">
        <div className="info-row">
          <i className="info-row__icon pi pi-phone" aria-hidden="true" />
          <div className="info-row__content">
            <div className="info-row__value">{formatUsPhone(client.phone)}</div>
          </div>
        </div>
      </div>

      <div className="mt-md">
        <div className="info-row">
          <i className="info-row__icon pi pi-envelope" aria-hidden="true" />
          <div className="info-row__content">
            <div className="info-row__value info-row__value--link" title={client.email}>{client.email}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AgencyCard({ agency, agent }: { agency: Agency; agent: Agent }) {
  return (
    <div></div>
    // <div className="card card--md">
    //   <div className="entity-header">
    //     <div className="entity-header__icon entity-header__icon--success">
    //       <i className="pi pi-shield" aria-hidden="true" />
    //     </div>
    //     <div className="entity-header__main">
    //       <div className="entity-header__name truncate">{agency.name}</div>
    //       <div className="entity-header__sub">{agency.typeLabel}</div>
    //     </div>
    //   </div>

    //   <div className="divider" />

    //   <div className="info-row">
    //     <i className="info-row__icon pi pi-user" aria-hidden="true" />
    //     <div className="info-row__content">
    //       <div className="info-row__label">Your Agent</div>
    //       <div className="info-row__value">{agent.name}</div>
    //       <div className="license-badge mt-xs">
    //         <i className="pi pi-verified" aria-hidden="true" />
    //         License: {agent.licenseNumber}
    //       </div>
    //     </div>
    //   </div>

    //   <div className="mt-md">
    //     <div className="info-row">
    //       <i className="info-row__icon pi pi-phone" aria-hidden="true" />
    //       <div className="info-row__content">
    //         <div className="info-row__value">{agent.phone}</div>
    //       </div>
    //     </div>
    //   </div>

    //   <div className="mt-md">
    //     <div className="info-row">
    //       <i className="info-row__icon pi pi-envelope" aria-hidden="true" />
    //       <div className="info-row__content">
    //         <div className="info-row__value info-row__value--link">{agent.email}</div>
    //       </div>
    //     </div>
    //   </div>
    // </div>
  );
}

function CertificateSummary({
  total, active,
  title = 'Certificate Summary'
}: {
  total: number; active: number; title?: string;
}) {
  return (
    <div className="card card--md app-details__summary">
      <div className="card__header">
        <div className="card__title" style={{ color: '#111827' }}>{title}</div>
      </div>
      <div className="stat-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <div className="stat-tile stat-tile--primary">
          <i className="stat-tile__icon pi pi-check-circle" aria-hidden="true" />
          <div className="stat-tile__value">{total}</div>
          <div className="stat-tile__label">Total Issued</div>
        </div>
        <div className="stat-tile stat-tile--success">
          <i className="stat-tile__icon pi pi-file" aria-hidden="true" />
          <div className="stat-tile__value">{active}</div>
          <div className="stat-tile__label">Active</div>
        </div>
      </div>
    </div>
  );
}

export default function DetailsSidebar({ client, agency, agent }: DetailsSidebarProps) {
  const { viewMode } = useViewMode();
  const { client: contextClient } = useSelectedClient();
  const resolvedClient = contextClient ?? client;
  const { summary, loading, error } = useCertificateSummary(resolvedClient.id);

  return (
    <aside className="app-details" aria-label="Client details">
      {/* <ViewToggle /> */}

      <div className="app-details__stack">
        <ClientCard client={client} />

        {viewMode === 'insured' && <AgencyCard agency={agency} agent={agent} />}

        {loading ? (
          <div className="card card--md app-details__summary">
            <SkeletonBlock lines={1} width="60%" height="1.5rem" className="mb-md" />
            <div className="stat-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
              <div className="stat-tile">
                <SkeletonBlock lines={1} width="100%" height="48px" />
              </div>
              <div className="stat-tile">
                <SkeletonBlock lines={1} width="100%" height="48px" />
              </div>
            </div>
          </div>
        ) : error ? (
          <div className="card card--md app-details__summary"><ErrorState compact /></div>
        ) : summary ? (
          <CertificateSummary
            total={summary.total}
            active={summary.active}
            title={viewMode === 'insured' ? 'Your Certificates' : 'Certificate Summary'}
          />
        ) : null}
      </div>
    </aside>
  );
}
